import React, { useState, useEffect, useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../../supabase/supabase';

const { FiLoader, FiList, FiClock, FiUser, FiActivity } = FiIcons;

export default function PlatformAdminAuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('d2f_audit_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(150);

      if (error) throw error;
      setLogs(data || []);
    } catch (err) {
      console.error('[AuditLog] Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getActionColor = (action) => {
    if (action.includes('approve')) return 'text-green-400 bg-green-400/10';
    if (action.includes('reject')) return 'text-red-400 bg-red-400/10';
    if (action.includes('update') || action.includes('edit')) return 'text-blue-400 bg-blue-400/10';
    if (action.includes('delete') || action.includes('remove')) return 'text-red-500 bg-red-500/10';
    return 'text-neutral-300 bg-neutral-800';
  };

  return (
    <div className="space-y-4">
      <div className="bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-neutral-800 bg-black/30 flex items-center gap-2 text-sm text-neutral-400">
          <SafeIcon icon={FiList} /> Showing last {logs.length} system events
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center p-12 text-neutral-400">
            <SafeIcon icon={FiLoader} className="animate-spin text-2xl mr-3" />
            Loading audit trails...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="bg-black/50 text-neutral-400">
                <tr>
                  <th className="px-6 py-4 font-medium">Timestamp</th>
                  <th className="px-6 py-4 font-medium">Actor</th>
                  <th className="px-6 py-4 font-medium">Action</th>
                  <th className="px-6 py-4 font-medium">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 text-neutral-400">
                      <div className="flex items-center gap-2 text-xs">
                        <SafeIcon icon={FiClock} className="text-neutral-500" />
                        {new Date(log.created_at).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-white font-medium text-xs">{log.actor_email || 'System'}</span>
                        <span className="text-neutral-600 text-[10px] font-mono mt-0.5">{log.actor_id ? log.actor_id.substring(0,8) + '...' : 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded text-xs font-medium inline-block ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-neutral-300 text-xs max-w-md truncate" title={log.note}>
                        {log.note || '-'}
                      </div>
                      {log.target_id && (
                        <div className="text-neutral-600 text-[10px] font-mono mt-1">
                          Target: {log.target_id}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-neutral-500">
                      No audit logs recorded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}